/**
 * HLOS-323: TS-side bridge para TTS nativo iOS via AVSpeechSynthesizer.
 *
 * O Swift `AppleTTSRuntime` (chamado via `AppleLLMRuntimeBridge` com
 * método "ttsSpeak") substitui o Web Speech Synthesis API do WKWebView
 * (que tem issues conhecidos de volume/voices no iOS).
 *
 * Este módulo é iOS-only. Web/Android continuam usando Web Speech API
 * via useVoiceOutput (fallback).
 */
import { Capacitor } from "@capacitor/core";

export const DEFAULT_TTS_TIMEOUT_MS = 10_000;

export interface TTSOptions {
  text: string;
  lang?: string;
  /** 0.0..1.0 (mapeado para AVSpeechUtterance.rate) */
  rate?: number;
  /** 0.5..2.0 (AVSpeechUtterance.pitchMultiplier) */
  pitch?: number;
}

export class AppleTTSBridge {
  /** iOS-only. */
  isAvailable(): boolean {
    if (typeof window === "undefined") return false;
    const w = window as unknown as { webkit?: { messageHandlers?: { hlos?: unknown } } };
    return Capacitor.getPlatform() === "ios" && !!w.webkit?.messageHandlers?.hlos;
  }

  private isSpeakingInternal = false;
  /** estado público para o composable useVoiceOutput. */
  isSpeaking(): boolean {
    return this.isSpeakingInternal;
  }

  private setSpeaking(v: boolean): void {
    this.isSpeakingInternal = v;
  }

  /** Para a síntese atual. */
  async stop(): Promise<void> {
    if (!this.isAvailable()) return;
    try {
      const w = window as unknown as { webkit: { messageHandlers: { hlos: { postMessage: (m: unknown) => void } } } };
      w.webkit.messageHandlers.hlos.postMessage({ id: this.nextId(), method: "ttsStop" });
      this.setSpeaking(false);
    } catch (e) {
      console.warn("[appleTTS] stop failed:", e);
    }
  }

  /**
   * Fala o texto. Resolve quando Swift confirma que começou
   * (didStart). didFinish/didCancel/didError chegam depois mas o
   * composable observa via onstart/ondone.
   */
  async speak(opts: TTSOptions): Promise<void> {
    if (!this.isAvailable()) {
      throw new Error("AppleTTSBridge not available");
    }
    const w = window as unknown as { webkit: { messageHandlers: { hlos: { postMessage: (m: unknown) => void } } } };
    w.webkit.messageHandlers.hlos.postMessage({
      id: this.nextId(),
      method: "ttsSpeak",
      text: opts.text,
      lang: opts.lang ?? "pt-BR",
      rate: opts.rate ?? 0.5,
      pitch: opts.pitch ?? 1.0,
    });
    this.setSpeaking(true);
    // Auto-stop após timeout para evitar estados pendentes
    setTimeout(() => {
      this.setSpeaking(false);
    }, DEFAULT_TTS_TIMEOUT_MS);
  }

  private nextId(): string {
    return `tts_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
  }
}

export const appleTTSBridge = new AppleTTSBridge();
